import {
  $framework,
  catchError,
  useSource,
  useTranslation,
} from "@opendash/core";
import { Icon } from "@opendash/icons";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { useNavigate } from "@opendash/router";
import { scheduleIterator } from "@opendash/schedule";
import { Button, Flex, Modal, Space, Table, Tag } from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { SourceTagsForScheduleTable, StepMedia, StepsTable } from "..";
import {
  Schedule_Execution,
  Schedule_Template,
} from "../../../js/parsegenerated";

type Props = {
  showChildren?: boolean;
};

interface TemplateDataType {
  schedule: Schedule_Template;
  lastexecution: Date | undefined;
}

export const ScheduleTemplatesTable = observer((props: Props) => {
  const t = useTranslation();
  const [source] = useSource();
  const { confirm } = Modal;
  const [scheduleTemplates, setScheduleTemplates] = useState<
    TemplateDataType[]
  >([]);
  const navigate = useNavigate();

  const columns: ColumnsType<TemplateDataType> = [
    {
      title: t("maintenance:schedule.source.columns.name.label"),
      dataIndex: "name",
      render: (_, record) => <span>{record.schedule.get("title")}</span>,
    },
    {
      title: t("maintenance:schedule.source.columns.description.label"),
      dataIndex: "description",
      render: (_, record) => <span>{record.schedule.get("description")}</span>,
    },
    {
      title: t("maintenance:schedule.source.columns.startdate.label"),
      dataIndex: "startdate",
      render: (_, record) => (
        <span>
          {dayjs(record.schedule.get("cron")?.timestamp?.startDate).format(
            "DD.MM.YYYY"
          )}
        </span>
      ),
    },
    {
      title: t("maintenance:schedule.source.columns.enddate.label"),
      dataIndex: "enddate",
      render: (_, record) => (
        <span>
          {record.schedule.get("cron") !== undefined &&
          record.schedule.get("cron")?.timestamp?.endDate !== undefined
            ? dayjs(record.schedule.get("cron")?.timestamp?.endDate).format(
                "DD.MM.YYYY"
              )
            : t("maintenance:schedule.source.columns.enddate.never")}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "status",
      width: 200,
      render: (_, record) => (
        <Flex gap="middle" wrap="wrap">
          <SourceTagsForScheduleTable record={record.schedule} />
          {getlastexecution(record.schedule)}
          {getnextexecution(record.schedule)}
        </Flex>
      ),
    },
    {
      title: t("maintenance:schedule.source.columns.action.label"),
      dataIndex: "action",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            onClick={() => {
              confirm({
                title: t(
                  "maintenance:schedule.source.columns.action.execute.confirm.title"
                ),
                icon: <Icon icon="fa:question"></Icon>,
                content: t(
                  "maintenance:schedule.source.columns.action.execute.confirm.content"
                ),
                okText: t("maintenance:global.button.ok_label"),
                cancelText: t("maintenance:global.button.cancel_label"),
                onOk() {
                  executeconfirm(record.schedule);
                },
                onCancel() {},
              });
            }}
            icon={<Icon icon="fa:check"></Icon>}
          >
            {t("maintenance:schedule.source.columns.action.execute.tooltip")}
          </Button>
        </Space>
      ),
    },
  ];

  const executeconfirm = async (schedule: Schedule_Template) => {
    //Get selected source of the schedule
    const [schedulesourceerror, schedulesource] = await catchError(
      schedule.get("sources").query().equalTo("objectId", source.id).find()
    );

    //Check if lenth of source is not 1
    if (schedulesource.length !== 1) {
      return;
    }

    //Execute the schedule
    // Create new schedule_execution object
    const scheduleExecution = new Schedule_Execution();
    const savedscheduleobject = await scheduleExecution.save({
      description: schedule.get("description"),
      source: schedulesource[0],
      tenant: schedule.get("tenant"),
      title: schedule.get("title"),
      user: Parse.User.current()!,
      origin: schedule,
      origin_cron: schedule.get("cron"),
    });

    navigate(`/openservice/schedule/${savedscheduleobject.id}/execute`);
  };

  const getlastexecution = (record: Schedule_Template) => {
    const scheduletemplate = scheduleTemplates.find((value) => {
      return value.schedule.id === record.id;
    });

    if (scheduletemplate && scheduletemplate.lastexecution) {
      return (
        <Tag color="green">
          {t("maintenance:schedule.source.columns.action.lastexecution.label", {
            date: dayjs(scheduletemplate.lastexecution).format(
              "DD.MM.YYYY HH:mm"
            ),
          })}
        </Tag>
      );
    }
    return (
      <Tag color="gold">
        {t("maintenance:schedule.source.columns.action.lastexecution.label", {
          date: t(
            "maintenance:schedule.source.columns.action.lastexecution.never"
          ),
        })}
      </Tag>
    );
  };

  const getnextexecution = (record: Schedule_Template) => {
    if (!record.get("cron")) {
      return (
        <Tag color="blue">
          {t("maintenance:schedule.source.columns.action.nextexecution.never")}
        </Tag>
      );
    }

    const scheduletemplate = scheduleTemplates.find((value) => {
      return value.schedule.id === record.id;
    });

    const iterator = scheduleIterator(record.get("cron"));
    const nextdate = iterator?.nextDate(scheduletemplate.lastexecution);

    return (
      <Tag color="blue">
        {t("maintenance:schedule.source.columns.action.nextexecution.label", {
          date: nextdate
            ? dayjs(nextdate).format("DD.MM.YYYY")
            : t(
                "maintenance:schedule.source.columns.action.nextexecution.never"
              ),
        })}
      </Tag>
    );
  };

  useEffect(() => {
    if (source === undefined) {
      return;
    }

    const sourceIds = [source.id];

    if (props.showChildren) {
      const subordinates =
        $framework.services.SourceService.getDescendents(source).map(
          (s) => s.id
        ) || [];
      sourceIds.push(...subordinates);
    }

    // Get all schedule templates for the selected source or subordinate sources if showChildren is true
    new Parse.Query(Schedule_Template)
      .matchesQuery(
        "sources",
        new Parse.Query(Source).containedIn("objectId", sourceIds)
      )
      .equalTo("enabled", true)
      .include("steps")
      .find()
      .then((values) => {
        const tabledatapromises = values.map(async (value) => {
          // Get the last execution schedule
          const [error, executionvalues] = await catchError(
            new Parse.Query(Schedule_Execution)
              .descending("createdAt")
              .equalTo("origin", {
                className: Schedule_Template.className,
                __type: "Pointer",
                objectId: value.id,
              })
              .limit(1)
              .find()
          );

          return {
            schedule: value,
            lastexecution:
              executionvalues.length > 0
                ? executionvalues[0].get("createdAt")
                : undefined,
          };
        });

        Promise.all(tabledatapromises).then(setScheduleTemplates);
      });
  }, [source.id, props.showChildren]);

  return (
    <>
      {/* <Typography.Title level={3}>
        {t("maintenance:schedule.source.templates.headline")}
      </Typography.Title> */}
      <Table
        columns={columns}
        dataSource={scheduleTemplates}
        size="small"
        rowKey={(record: TemplateDataType) => record.schedule.id}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <>
                <StepMedia scheduletemplate={record.schedule} />
                <StepsTable schedule={record.schedule} />
              </>
            );
          },
          rowExpandable: (record) => {
            return true;
          },
        }}
      />
    </>
  );
});
